import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Avatar
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import teamService, { Team } from '../services/teamService';
import { toast } from 'react-toastify';

const TeamRequestAccessPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTeamDetails();
  }, [id]);

  const fetchTeamDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await teamService.getTeamById(parseInt(id));
      setTeam(response);
    } catch (error) {
      console.error('Error fetching team details:', error);
      setError('Failed to load team details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Please provide a reason for joining');
      return;
    }

    try {
      setSubmitting(true);
      await teamService.requestTeamAccess({
        teamId: parseInt(id!),
        message: message.trim()
      });
      
      toast.success('Access request submitted successfully!');
      navigate('/teams');
    } catch (error: any) {
      console.error('Error submitting request:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit request';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate('/teams')}
            sx={{ mt: 2 }}
          >
            Back to Teams
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Button 
            color="inherit" 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/teams')}
          >
            Back to Teams
          </Button>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            Request Team Access
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </Avatar>
            <Button color="inherit" onClick={handleLogout}>Logout</Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              Request to Join Team
            </Typography>
            
            {team && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  {team.name}
                </Typography>
                {team.location && (
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Location: {team.location}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Department: {team.departmentName || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {team.status}
                </Typography>
              </Box>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Why do you want to join this team?"
                placeholder="Please explain why you would like to join this team and how you can contribute..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting || !message.trim()}
                  fullWidth
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/teams')}
                  disabled={submitting}
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TeamRequestAccessPage;